import { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';

// Public Trustpilot review URL — used by the CTA buttons.
// Strip any tracking params when sharing publicly.
export const TRUSTPILOT_URL = 'https://uk.trustpilot.com/review/tradesmateuk.co.uk';

// TrustBox — Review Collector widget.
export const TRUSTPILOT_BUSINESS_UNIT_ID = '6a359ad49a89ab506eaa6947';
export const TRUSTPILOT_TEMPLATE_ID = '56278e9abfbbba0bdcd568bc'; // Review Collector
export const TRUSTPILOT_TOKEN = '055fdb77-98f3-4128-88fa-f590b81e9a2b';
export const TRUSTPILOT_LOCALE = 'en-GB';

/**
 * Compact Trustpilot CTA — five stars + "Review us on Trustpilot" link.
 * Always renders. Safe to drop into hero, footer, public quote page, etc.
 */
export function TrustpilotCTA({ variant = 'light', className = '' }) {
  const dark = variant === 'dark';
  return (
    <a
      href={TRUSTPILOT_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="trustpilot-cta"
      className={`inline-flex items-center gap-2 group ${className}`}
    >
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="bg-[#00b67a] inline-flex items-center justify-center"
            style={{ width: 18, height: 18 }}
          >
            <Star size={12} fill="#fff" stroke="#fff" strokeWidth={1} />
          </span>
        ))}
      </span>
      <span className={`font-mono text-xs uppercase tracking-widest ${dark ? 'text-slate-300 group-hover:text-white' : 'text-slate-700 group-hover:text-brand'}`}>
        Review us on
        <span className="font-display font-semibold ml-1" style={{ color: dark ? '#fff' : '#00b67a' }}>
          Trustpilot
        </span>
      </span>
    </a>
  );
}

/**
 * Embedded TrustBox — renders the live Trustpilot widget. The bootstrap
 * script in index.html provides window.Trustpilot which paints the
 * .trustpilot-widget div. We re-process on mount so it also works after
 * client-side (SPA) navigation.
 */
export function TrustpilotWidget({ template, height = '52px', width = '100%', theme = 'light', className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    let tries = 0;
    const paint = () => {
      if (window.Trustpilot && ref.current) {
        window.Trustpilot.loadFromElement(ref.current, true);
        return true;
      }
      return false;
    };
    if (!paint()) {
      const t = setInterval(() => {
        tries += 1;
        if (paint() || tries > 40) clearInterval(t);
      }, 250);
      return () => clearInterval(t);
    }
  }, []);

  if (!TRUSTPILOT_BUSINESS_UNIT_ID) return null;

  return (
    <div
      ref={ref}
      className={`trustpilot-widget ${className}`}
      data-testid="trustpilot-widget"
      data-locale={TRUSTPILOT_LOCALE}
      data-template-id={template || TRUSTPILOT_TEMPLATE_ID}
      data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
      data-style-height={height}
      data-style-width={width}
      data-theme={theme}
      data-token={TRUSTPILOT_TOKEN}
    >
      <a href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
    </div>
  );
}
